import React, {useState} from "react";
import {GpsShapeType, LatLong} from "../types";
import {DrawingMode, DrawResult} from "../map/drawingTypes";
import {MapShape} from "../map/MapTypes";

export type MapContextI = {
    setTriggerSelection: (id: string | undefined) => void
    getTriggerSelection: () => string | undefined

    setSelectedShapeType: (shapeType: GpsShapeType | undefined) => void;
    getSelectedShapeType: () => GpsShapeType | undefined

    setEditingPolygonPath: (path: LatLong[]) => void
    getEditingPolygonPath: () => LatLong[] | undefined

    setDrawingModes: (drawingModes: DrawingMode[]) => void
    getDrawingModes: () => DrawingMode[]

    setActiveDrawingMode: (drawingMode: DrawingMode | undefined) => void
    getActiveDrawingMode: () => DrawingMode | undefined

    setOnOverlayComplete: (onOverlayComplete: (drawResult: DrawResult) => void) => void
    getOnOverlayComplete: () => (drawResult: DrawResult) => void

    addOrReplaceMapShape: (shape: MapShape) => void,
    addOrReplaceMapShapes: (shape: MapShape[]) => void,
    replaceMapShapeGroup: (isGroupMember: (shape: MapShape) => boolean, newShapes: MapShape[]) => void,
    getMapShapes: () => MapShape[]

    setMapCenter: (center: LatLong) => void
    getMapCenter: () => LatLong | undefined

    setMapLatSpan: (span: number) => void
    getMapLatSpan: () => number | undefined

    setActivePanel: (panel: string | undefined) => void
    getActivePanel: () => string | undefined
}

export const MapContext = React.createContext<MapContextI>({
    setTriggerSelection: () => null,
    getTriggerSelection: () => undefined,

    setSelectedShapeType: () => null,
    getSelectedShapeType: () => undefined,

    setEditingPolygonPath: () => null,
    getEditingPolygonPath: () => undefined,

    setDrawingModes: () => undefined,
    getDrawingModes: () => [],

    setActiveDrawingMode: () => null,
    getActiveDrawingMode: () => undefined,

    setOnOverlayComplete: () => null,
    getOnOverlayComplete: () => () => undefined,

    addOrReplaceMapShape: () => undefined,
    addOrReplaceMapShapes: () => undefined,
    replaceMapShapeGroup: () => undefined,
    getMapShapes: () => [],

    setMapCenter: () => undefined,
    getMapCenter: () => undefined,

    setMapLatSpan: () => undefined,
    getMapLatSpan: () => undefined,

    setActivePanel: () => undefined,
    getActivePanel: () => undefined
});

export const MapContextWrapper = ({children}: { children: any }) => {
    const [triggerSelection, setTriggerSelection] = useState<string | undefined>(undefined);

    const [selectedGpsShapeType, setSelectedShapeType] = useState<GpsShapeType | undefined>(undefined);

    const [editingPolygonPath, setEditingPolygonPath] = useState<LatLong[]>();
    const [drawingModes, setDrawingModes] = useState<DrawingMode[]>([]);
    const [activeDrawingMode, setActiveDrawingMode] = useState<DrawingMode | undefined>();

    const [onOverlayComplete, setOnOverlayComplete] = useState<(() => (drawResult: DrawResult) => void) | undefined>();

    const [mapShapes, setMapShapes] = useState<MapShape[]>([]);
    const [mapCenter, setMapCenter] = useState<LatLong | undefined>(undefined);
    const [mapLatSpan, setMapLatSpan] = useState<number | undefined>(undefined);
    const [activePanel, setActivePanel] = useState<string | undefined>(undefined);

    return <MapContext.Provider value={
        {
            setTriggerSelection,
            getTriggerSelection: () => triggerSelection,

            setSelectedShapeType,
            getSelectedShapeType: () => selectedGpsShapeType,

            getEditingPolygonPath: () => editingPolygonPath,
            setEditingPolygonPath,

            setDrawingModes: (modes: DrawingMode[]) => {
                setDrawingModes(modes);
            },
            getDrawingModes: () => drawingModes,

            setActiveDrawingMode,
            getActiveDrawingMode: () => activeDrawingMode,

            // Needs to be wrapped in a call to avoid the default to be triggered
            setOnOverlayComplete: (newOnOverlayComplete: (drawResult: DrawResult) => void) => {
                setOnOverlayComplete(() => newOnOverlayComplete)
            },
            getOnOverlayComplete: () => {
                return onOverlayComplete ?
                    onOverlayComplete :
                    () => undefined
            },

            getMapShapes: () => mapShapes,
            addOrReplaceMapShape: (newShape: MapShape) => {
                setMapShapes(prev => {
                    const shapes = prev.filter((shape: MapShape) => shape.id !== newShape.id);
                    return [...shapes, newShape];
                });
            },
            addOrReplaceMapShapes: (newShapes: MapShape[]) => {
                setMapShapes(prev => {
                    const otherShapes = prev.filter((shape: MapShape) => {
                        return newShapes.findIndex((shp) => shp.id === shape.id) === -1;
                    });
                    return [...otherShapes, ...newShapes];
                });
            },
            replaceMapShapeGroup: (isGroupMember: (shape: MapShape) => boolean, newShapes: MapShape[]) => {
                setMapShapes(prev => {
                    const kept = prev.filter(shape => !isGroupMember(shape));
                    return [...kept, ...newShapes];
                });
            },

            setMapCenter,
            getMapCenter: () => mapCenter,

            setMapLatSpan,
            getMapLatSpan: () => mapLatSpan,

            setActivePanel,
            getActivePanel: () => activePanel
        }
    }>
        {children}
    </MapContext.Provider>
}
